/* ═══════════════════════════════════════════════════
   TrackLab v2 — Tooltip System

   Desktop (>700px): click, or keyboard focus, opens a positioned tooltip bubble
   Mobile  (≤700px): tap opens bottom-sheet (reuses mob-tip-sheet)

   Usage: <span class="tl-tip" data-tip-title="Score" data-tip-body="Our model rates..."
          data-tip-href="/tracklab-score/" tabindex="0">?</span>
   Title, body and link are written with textContent, never innerHTML.
   ═══════════════════════════════════════════════════ */

import { openSheet } from './sheet.js';

(function () {
  'use strict';

  var MOBILE_BP = 700;
  var TIP_LINK_TEXT = 'What is the TrackLab Score?';
  var activeBubble = null;
  var openedByFocus = false;

  function isMobile() {
    return window.innerWidth <= MOBILE_BP;
  }

  // Only same-site paths ("/x", not "//x") become links in a tip.
  function appendLink(parent, trigger) {
    var href = trigger.getAttribute('data-tip-href') || '';
    if (href.charAt(0) !== '/' || href.charAt(1) === '/') return;
    var link = document.createElement('a');
    link.className = 'tl-tip-link';
    link.href = href;
    link.textContent = TIP_LINK_TEXT;
    parent.appendChild(link);
  }

  // ─── Desktop: positioned bubble ───

  function showBubble(trigger) {
    hideBubble();

    var bubble = document.createElement('div');
    bubble.className = 'tl-tip-bubble';

    var title = document.createElement('div');
    title.className = 'tl-tip-title';
    title.textContent = trigger.getAttribute('data-tip-title') || '';

    var body = document.createElement('div');
    body.className = 'tl-tip-body';
    body.textContent = trigger.getAttribute('data-tip-body') || '';

    bubble.appendChild(title);
    bubble.appendChild(body);
    appendLink(bubble, trigger);

    document.body.appendChild(bubble);
    activeBubble = bubble;

    // Position above the trigger, centred
    var rect = trigger.getBoundingClientRect();
    var bw = bubble.offsetWidth;
    var bh = bubble.offsetHeight;
    var left = rect.left + rect.width / 2 - bw / 2;
    var top = rect.top - bh - 8 + window.scrollY;

    // Keep within viewport
    if (left < 8) left = 8;
    if (left + bw > window.innerWidth - 8) left = window.innerWidth - bw - 8;
    if (top < window.scrollY + 8) {
      // Show below instead
      top = rect.bottom + 8 + window.scrollY;
    }

    bubble.style.left = left + 'px';
    bubble.style.top = top + 'px';
    bubble.style.opacity = '1';
  }

  function hideBubble() {
    if (activeBubble) {
      activeBubble.remove();
      activeBubble = null;
    }
  }

  // ─── Mobile: bottom-sheet ───

  var releaseSheet = null;

  function closeSheet() {
    var sheet = document.querySelector('.mob-tip-sheet');
    var scrim = document.querySelector('.mob-tip-scrim');
    if (sheet) sheet.remove();
    if (scrim) scrim.remove();
    if (releaseSheet) { var release = releaseSheet; releaseSheet = null; release(); }
  }

  function showSheet(trigger) {
    closeSheet();
    var title = trigger.getAttribute('data-tip-title') || 'Info';

    var scrim = document.createElement('div');
    scrim.className = 'mob-tip-scrim';
    scrim.addEventListener('click', closeSheet);
    document.body.appendChild(scrim);

    var sheet = document.createElement('div');
    sheet.className = 'mob-tip-sheet m-sheet';

    var header = document.createElement('div');
    header.className = 'mob-tip-sheet-header';
    var titleEl = document.createElement('span');
    titleEl.className = 'mob-tip-sheet-title';
    titleEl.textContent = title;
    var close = document.createElement('button');
    close.className = 'mob-tip-sheet-close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    header.appendChild(titleEl);
    header.appendChild(close);

    var body = document.createElement('div');
    body.className = 'mob-tip-sheet-body';
    body.textContent = trigger.getAttribute('data-tip-body') || '';
    appendLink(body, trigger);

    sheet.appendChild(header);
    sheet.appendChild(body);

    close.addEventListener('click', closeSheet);
    document.body.appendChild(sheet);
    // A dialog: scroll lock, focus inside (Tab stays in), focus back to the "?" (Esc is handled below)
    releaseSheet = openSheet(sheet, { label: title, opener: trigger, scrim: scrim });
  }

  // Keyboard open: always opens, never toggles closed.
  function openTip(tip) {
    if (isMobile()) {
      showSheet(tip);
    } else {
      showBubble(tip);
    }
  }

  // Keyboard focus counts only when the browser shows a focus ring, so a
  // mouse click (which also focuses the span) keeps the click toggle below.
  function isKeyboardFocus(el) {
    try {
      return el.matches(':focus-visible');
    } catch (err) {
      return false;
    }
  }

  // ─── Event delegation ───

  document.addEventListener('click', function (e) {
    var tip = e.target.closest('.tl-tip');
    if (!tip) {
      hideBubble();
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (isMobile()) {
      showSheet(tip);
    } else {
      if (activeBubble) {
        hideBubble();
      } else {
        showBubble(tip);
      }
    }
  }, true);

  document.addEventListener('focusin', function (e) {
    var tip = e.target.closest ? e.target.closest('.tl-tip') : null;
    if (!tip || isMobile() || !isKeyboardFocus(tip)) return;
    openedByFocus = true;
    showBubble(tip);
  });

  document.addEventListener('focusout', function () {
    if (!openedByFocus) return;
    openedByFocus = false;
    hideBubble();
  });

  // Close desktop bubble on scroll or escape; Enter/Space open a focused tip.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      openedByFocus = false;
      hideBubble();
      closeSheet();
      return;
    }
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var tip = e.target.closest ? e.target.closest('.tl-tip') : null;
    if (!tip) return;
    e.preventDefault();
    openTip(tip);
  });

  window.addEventListener('scroll', hideBubble, { passive: true });
})();
